/**
 * In-app help-dialog die de handleiding rendert: in het Nederlands
 * `docs/user-guide.md`, in het Engels de hoofdstukken van
 * `docs/user-guide/` (zie `userGuides.ts`), naar de UI-taal.
 *
 * De markdown wordt via Vite's `?raw` import-suffix als string in de
 * bundle opgenomen — geen runtime-fetch nodig en de inhoud is altijd
 * de versie die op het moment van build in de repo stond. Bij elke
 * doc-update + redeploy krijgen gebruikers automatisch de nieuwe versie.
 *
 * Rendering via `react-markdown` + `remark-gfm` voor tabellen,
 * task-lists en strikethrough — features die de user-guide gebruikt.
 *
 * Tailwind utility-classes geven de elementen typografische opmaak
 * (geen `@tailwindcss/typography` plugin nodig — dat zou een extra
 * dep zijn voor één scherm).
 */
import { versionLabel } from "@ae/api-client";
import { type ComponentProps, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { fetchServerVersion, WEB_VERSION_LABEL } from "../buildInfo";
import {
  dutchGuide,
  englishChapters,
  guideFor,
  guideImageUrl,
  resolveGuideLink,
} from "./userGuides";

const CHAPTERS = englishChapters();

interface Props {
  open: boolean;
  onClose: () => void;
}

export function HelpDialog({ open, onClose }: Props) {
  const { t, i18n } = useTranslation();
  const closeRef = useRef<HTMLButtonElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const which = guideFor(i18n.language);
  const [serverLabel, setServerLabel] = useState<string | null>(null);

  // De versie van de server, als die afwijkt van de web-app: die worden los
  // van elkaar gedeployd.
  useEffect(() => {
    if (!open) return;
    void fetchServerVersion().then((v) => {
      const label = v ? versionLabel(v.version, v.build) : null;
      setServerLabel(label && label !== WEB_VERSION_LABEL ? label : null);
    });
  }, [open]);

  // Focus de sluit-knop bij open zodat Tab + Esc onmiddellijk werken.
  useEffect(() => {
    if (open) closeRef.current?.focus();
  }, [open]);

  // Esc sluit het venster.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="ledger fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6"
      onClick={(e) => {
        // Klik op de backdrop sluit; klikken binnen de panel niet.
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="help-title"
    >
      <div
        className="flex h-full w-full max-w-4xl flex-col rounded-lg border border-(--ld-control) bg-(--ld-surface) shadow-xl"
        // Klikken in panel mag niet de backdrop-onClick triggeren
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <header className="flex shrink-0 items-center justify-between border-(--ld-border) border-b px-4 py-2">
          <h2 id="help-title" className="font-semibold text-(--ld-text) text-sm">
            {t("help.title")}
          </h2>
          <button
            ref={closeRef}
            type="button"
            className="rounded px-2 py-0.5 text-(--ld-muted) text-sm hover:bg-(--ld-raised) hover:text-(--ld-text)"
            onClick={onClose}
            aria-label={t("settings.common.close")}
          >
            ✕
          </button>
        </header>
        <div ref={bodyRef} className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
          {/*
            Tailwind-utility-class typografie. Zelfde scope als
            `prose` zonder de plugin: rubrieken vet + groot, lijsten
            inspringen, code-blokken monospace + grijze achtergrond,
            tabellen met grenzen.
          */}
          <article
            className={[
              "max-w-none text-(--ld-text) text-sm leading-relaxed",
              "[&_h1]:mt-0 [&_h1]:mb-4 [&_h1]:font-semibold [&_h1]:text-2xl [&_h1]:text-(--ld-text)",
              "[&_h2]:mt-6 [&_h2]:mb-2 [&_h2]:font-semibold [&_h2]:text-base [&_h2]:text-(--ld-text)",
              "[&_h3]:mt-4 [&_h3]:mb-2 [&_h3]:font-semibold [&_h3]:text-base [&_h3]:text-(--ld-text)",
              "[&_p]:my-2",
              "[&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-6",
              "[&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-6",
              "[&_li]:my-0.5",
              "[&_a]:text-(--ld-accent) [&_a]:underline hover:[&_a]:text-(--ld-accent)",
              "[&_code]:rounded [&_code]:bg-(--ld-active) [&_code]:px-1 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-[12px]",
              "[&_pre]:my-3 [&_pre]:overflow-auto [&_pre]:rounded [&_pre]:bg-(--ld-active) [&_pre]:p-3",
              "[&_pre_code]:bg-transparent [&_pre_code]:p-0",
              "[&_blockquote]:my-2 [&_blockquote]:border-(--ld-control) [&_blockquote]:border-l-4 [&_blockquote]:pl-3 [&_blockquote]:text-(--ld-muted)",
              "[&_table]:my-3 [&_table]:w-full [&_table]:border-collapse [&_table]:text-xs",
              "[&_th]:border [&_th]:border-(--ld-control) [&_th]:bg-(--ld-raised) [&_th]:px-2 [&_th]:py-1 [&_th]:text-left",
              "[&_td]:border [&_td]:border-(--ld-border) [&_td]:px-2 [&_td]:py-1 [&_td]:align-top",
              "[&_hr]:my-6 [&_hr]:border-(--ld-border)",
              "[&_strong]:font-semibold [&_strong]:text-(--ld-text)",
            ].join(" ")}
          >
            {which === "nl" ? (
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{dutchGuide}</ReactMarkdown>
            ) : (
              CHAPTERS.map((ch) => (
                <section
                  key={ch.file}
                  id={ch.anchor}
                  className="border-(--ld-border) border-b pb-6 last:border-b-0"
                >
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      a: (p) => <GuideLinkEl {...p} scrollArea={bodyRef} />,
                      img: ({ src, alt }) => {
                        const url = guideImageUrl(typeof src === "string" ? src : undefined);
                        return url ? (
                          <img src={url} alt={alt ?? ""} className="my-3 max-w-full" />
                        ) : null;
                      },
                    }}
                  >
                    {ch.markdown}
                  </ReactMarkdown>
                </section>
              ))
            )}
          </article>
        </div>
        <footer className="flex shrink-0 justify-between gap-4 border-(--ld-border) border-t bg-(--ld-raised) px-4 py-2 text-(--ld-muted) text-xs">
          <span>{t("help.versionNote")}</span>
          <span className="shrink-0 tabular-nums">
            {t("help.appVersion", { version: WEB_VERSION_LABEL })}
            {serverLabel ? ` · ${t("help.serverVersion", { version: serverLabel })}` : ""}
          </span>
        </footer>
      </div>
    </div>
  );
}

/**
 * Een link in de Engelse handleiding: naar een ander hoofdstuk scrolt binnen
 * de Help, naar buiten opent normaal, naar een document buiten de
 * handleiding (extensies, referentie) wordt gewone tekst.
 */
function GuideLinkEl(
  props: ComponentProps<"a"> & { scrollArea: React.RefObject<HTMLDivElement | null> },
) {
  const { href, children, scrollArea } = props;
  const link = resolveGuideLink(href);
  if (link.kind === "text") return <span>{children}</span>;
  if (link.kind === "keep") {
    const external = /^https?:/i.test(href ?? "");
    return (
      <a href={href} {...(external ? { target: "_blank", rel: "noreferrer" } : {})}>
        {children}
      </a>
    );
  }
  return (
    <a
      href={`#${link.anchor}`}
      onClick={(e) => {
        e.preventDefault();
        const target = scrollArea.current?.querySelector(`#${link.anchor}`);
        target?.scrollIntoView({ behavior: "smooth", block: "start" });
      }}
    >
      {children}
    </a>
  );
}
